import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import SideBlockAds from "@components/Blocks/Container/SideBlockAds";
import ShareSocial, {
  SocialVariant,
} from "@components/Blocks/ShareSocial/ShareSocial";
import { CAS_UGC_SECTION_BANNER_ID } from "@helper/getEnvVariables";
import { getMetaData } from "@helper/getMetaData";
import { Route } from "@route-types/UGC/UGCListingPage/+types/UGCListingPage";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import { NoSSR } from "@sphtech/web2-core/components";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import UGCLandingPageMetaData from "../UGCLandingPage/UGCLandingPageMetaData";
import CampaignStoriesList from "./UGCCampaignStoriesList";
import UGCEmptyListing from "./UGCEmptyListing";
import {
  fetchUGCListingData,
  type UGCListingPageResponse,
} from "./UGCListingPage.server";

export function meta() {
  return [getMetaData(UGCLandingPageMetaData().metaTags)];
}

export default function UGCListingPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    UGCListingPageResponse,
    string
  > = useLoaderData();
  if (
    dataLoaderResponse.type === ResponseType.SUCCESS &&
    dataLoaderResponse.payload.isCampignValid
  ) {
    return (
      <UGCEmptyListing
        sectionName="mata-mata"
        urlPath={dataLoaderResponse.payload.urlAlias}
        data={dataLoaderResponse.payload.data}
      />
    );
  }
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const articles = dataLoaderResponse.payload.data;
  const campaignTitle = articles[0]?.campaignReference?.title
    ? articles[0]?.campaignReference.title
    : "";
  const urlPath = dataLoaderResponse.payload.urlAlias;
  const sectionName = "mata-mata";
  const pageUrl = `${sectionName}/${urlPath}`;

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    hasLb1Ads: false,
    pageAdTargetValue: pageAdTargetValue,
  };

  const socialIconList = ["facebook", "whatsapp"];

  return (
    <BHBasePage {...basePageProps}>
      <div className="max-w-screen-bh-cxl m-auto">
        <CASBlock casId={CAS_UGC_SECTION_BANNER_ID} />
        <div className="text-lg font-primary">
          <div className="mb-sm font-bold">
            <a
              className="text-blue-300 hover:text-blue-700 hover:underline"
              href={`/${sectionName}`}
            >
              {sectionName}
            </a>
          </div>
          <div className="flex flex-col-reverse md:flex-row md:items-center justify-between lg:justify-start mb-xs">
            <div className="self-start w-full text-blue-100 font-bold">
              {campaignTitle}
            </div>
            <div className="self-end w-12/12 lg:w-6/12 flex gap-1">
              <NoSSR>
                {socialIconList.map((variant, index) => (
                  <ShareSocial
                    variant={variant as SocialVariant}
                    pageTitle={campaignTitle}
                    pagePath={pageUrl}
                    key={index}
                  />
                ))}
              </NoSSR>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row w-full gap-4">
          <CampaignStoriesList
            campaignStories={articles}
            urlPath={urlPath}
            sectionName={sectionName}
          />
          <SideBlockAds
            adsSectionName={sectionName}
            adsOnly={true}
            pageAdTargetingProps={pageAdTargetValue}
          />
        </div>
      </div>
    </BHBasePage>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  return await fetchUGCListingData(params.level1);
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<UGCListingPageResponse | null, string>
  >;
}
